import { useCallback, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAppState, type DailyPrompt } from '../lib/appState';
import { formatCountdown } from '../lib/countdown';
import { phrasePrompt } from '../../shared/prompt';
import { ApiError, submitPun } from '../lib/api';

const MAX_CHARS = 280;
/** The counter stays hidden until you are close to the limit. */
const COUNTER_VISIBLE_AT = 60;

/**
 * The prompt, and one box to answer it in.
 *
 * Before you post this is the whole game: no feed, no scores, nothing to
 * compare against. Committing first is what makes the day worth opening.
 */
function Today() {
  const navigate = useNavigate();
  const { prompt, promptFailed, submittedPun, checkingSubmission, onSubmitted, secondsLeft } =
    useAppState();

  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const remaining = MAX_CHARS - text.length;
  const canSubmit = text.trim().length > 0 && !submitting && prompt !== null;

  const handleSubmit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      if (!canSubmit) return;

      setSubmitting(true);
      setError(null);

      try {
        // The server picks the day, the prompt and the timestamp, sanitizes the
        // text, and rejects a duplicate atomically. Nothing here is trusted.
        await submitPun(text);
        onSubmitted(text.trim());
        setText('');
      } catch (err) {
        if (err instanceof ApiError) {
          setError(err.message);
          if (err.code === 'functions/already-exists') onSubmitted(text.trim());
        } else {
          console.error('[Today] submit failed:', err);
          setError('Could not post that. Give it another go?');
        }
      } finally {
        setSubmitting(false);
      }
    },
    [canSubmit, text, onSubmitted],
  );

  if (promptFailed) {
    return (
      <section className="rounded-2xl border-2 border-ink bg-white px-6 py-8 text-center shadow-pop">
        {/* Plain on purpose. Something is actually broken, and a joke here
            reads as the app not taking the problem seriously. */}
        <p className="font-display text-xl font-bold">No prompt today.</p>
        <p className="mt-2 text-sm text-ink/60">
          Something went wrong on our end. Refreshing usually sorts it out.
        </p>
      </section>
    );
  }

  return (
    <div>
      <PromptBoard prompt={prompt} />

      <div className="mt-10">
        {checkingSubmission ? (
          <div className="flex justify-center py-12">
            <span className="h-8 w-8 animate-spin rounded-full border-[3px] border-ink/15 border-t-ink" />
          </div>
        ) : submittedPun !== null ? (
          <LockedIn
            pun={submittedPun}
            countdown={formatCountdown(secondsLeft)}
            onSeeFeed={() => navigate('/feed')}
          />
        ) : (
          <>
            {error && (
              <p
                role="alert"
                className="mb-4 rounded-xl border-2 border-ink bg-coral px-4 py-3 text-sm font-medium text-white shadow-pop-sm"
              >
                {error}
              </p>
            )}

            <form onSubmit={handleSubmit}>
              <label htmlFor="pun" className="sr-only">
                Your pun
              </label>
              <textarea
                id="pun"
                value={text}
                onChange={(event) => setText(event.target.value.slice(0, MAX_CHARS))}
                placeholder="Something punderful..."
                rows={4}
                disabled={prompt === null}
                className="w-full resize-none rounded-2xl border-2 border-ink bg-white px-5 py-4 font-display text-xl leading-snug shadow-pop outline-none transition-shadow placeholder:font-sans placeholder:text-base placeholder:font-normal placeholder:text-ink/35 focus:shadow-pop-lg disabled:bg-ink/5"
              />

              <div className="mt-4 flex items-center justify-between gap-4">
                <span
                  className={`text-sm tabular-nums ${
                    remaining > COUNTER_VISIBLE_AT
                      ? 'invisible'
                      : remaining < 20
                        ? 'font-semibold text-coral'
                        : 'text-ink/50'
                  }`}
                >
                  {remaining} left
                </span>

                <button
                  type="submit"
                  disabled={!canSubmit}
                  className="press rounded-full border-2 border-ink bg-mustard px-7 py-3 font-display text-lg font-bold shadow-pop disabled:cursor-not-allowed disabled:bg-ink/10 disabled:text-ink/30 disabled:shadow-pop-sm"
                >
                  {submitting ? 'Posting...' : 'Lock it in'}
                </button>
              </div>
            </form>

            {prompt && <RobotNudge prompt={prompt} />}

            <p className="mt-10 border-t-2 border-dashed border-ink/20 pt-5 text-center text-sm text-ink/55">
              Today&rsquo;s puns unlock once yours is in. No pun intended. All puns intended.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * The prompt as a sentence rather than two labelled fields.
 *
 * `phrasePrompt` supplies the connecting words, including whether it is "a" or
 * "an", which matters because the output list contains both "App Name" and
 * "Restaurant Name".
 */
function PromptBoard({ prompt }: { prompt: DailyPrompt | null }) {
  const phrasing = prompt ? phrasePrompt(prompt.category, prompt.output) : null;

  return (
    <section>
      <h1 className="mb-5 text-center text-sm font-semibold text-ink/45">Today&rsquo;s prompt</h1>

      <div className="flex flex-col items-center gap-3 font-display text-2xl font-bold leading-tight sm:text-3xl">
        <span className="text-ink/60">{phrasing?.lead ?? 'Create a'}</span>
        <Tile colour="bg-coral text-white" tilt="-rotate-1.5" loading={!phrasing}>
          {phrasing?.output}
        </Tile>
        <span className="text-ink/60">{phrasing?.join ?? 'with a'}</span>
        <Tile colour="bg-grape text-white" tilt="rotate-1.5" loading={!phrasing}>
          {phrasing?.category}
        </Tile>
        <span className="text-ink/60">{phrasing?.tail ?? 'twist.'}</span>
      </div>
    </section>
  );
}

function Tile({
  children,
  colour,
  tilt,
  loading,
}: {
  children: ReactNode;
  colour: string;
  tilt: string;
  loading: boolean;
}) {
  if (loading) {
    return <span className={`h-12 w-56 animate-pulse rounded-xl border-2 border-ink/20 bg-ink/10 ${tilt}`} />;
  }

  return (
    <span className={`rounded-xl border-2 border-ink px-5 py-2 text-center shadow-pop ${colour} ${tilt}`}>
      {children}
    </span>
  );
}

/** Collapsed by default, because seeing an answer first spoils the puzzle. */
function RobotNudge({ prompt }: { prompt: DailyPrompt }) {
  const body = () => {
    if (prompt.aiStatus === 'success' && prompt.aiPun) {
      return <p className="font-display text-lg font-bold">&ldquo;{prompt.aiPun}&rdquo;</p>;
    }
    if (prompt.aiStatus === 'pending') {
      return <p className="text-sm text-ink/60">Still thinking. Robots are slow to the punch.</p>;
    }
    return (
      <p className="text-sm text-ink/60">
        The robot drew a blank. Punfortunately for it, you will not.
      </p>
    );
  };

  return (
    <details className="group mt-6 rounded-2xl border-2 border-dashed border-ink/30 bg-white/60">
      <summary className="cursor-pointer list-none px-5 py-3 text-sm font-semibold text-ink/70 marker:content-none group-open:text-ink">
        Stuck? Get some AI-nspiration
      </summary>
      <div className="border-t-2 border-dashed border-ink/20 px-5 py-4">
        {body()}
        <p className="mt-2 text-xs text-ink/50">Beat it. Low bar.</p>
      </div>
    </details>
  );
}

function LockedIn({
  pun,
  countdown,
  onSeeFeed,
}: {
  pun: string;
  countdown: string;
  onSeeFeed: () => void;
}) {
  return (
    <section className="text-center">
      <div className="relative rounded-2xl border-2 border-ink bg-white px-6 py-10 shadow-pop">
        <span className="absolute -top-4 left-1/2 -translate-x-1/2 animate-stamp-in whitespace-nowrap rounded-full border-2 border-ink bg-mustard px-4 py-1 font-display text-sm font-extrabold shadow-pop-sm">
          Locked in
        </span>

        <p className="font-display text-2xl font-bold leading-snug">&ldquo;{pun}&rdquo;</p>
      </div>

      <button
        onClick={onSeeFeed}
        className="press mt-8 rounded-full border-2 border-ink bg-lagoon px-7 py-3 font-display text-lg font-bold text-white shadow-pop"
      >
        See today&rsquo;s puns
      </button>

      <p className="mt-8 border-t-2 border-dashed border-ink/20 pt-5 text-sm text-ink/55">
        Well pun. Fresh prompt in {countdown}.
      </p>
    </section>
  );
}

export default Today;
